import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { Problem } from "../common/problem.js";
import { z } from "../zod.js";
import {
  OpportunityBoardQuery,
  OpportunityBoardResponse,
  OpportunityCreateRequest,
  OpportunityDetail,
  OpportunityForecastQuery,
  OpportunityForecastResponse,
  OpportunityListQuery,
  OpportunityListResponse,
  OpportunityLoseRequest,
  OpportunityMoveConflictExtensions,
  OpportunityMoveRequest,
  OpportunityUpdateRequest,
  OpportunityWinRequest,
  Opportunity,
  PipelineStage,
  PipelineStageListResponse,
  PipelineStageUpdateRequest,
  QuoteOpportunityLinkRequest,
  QuoteOpportunityLinkResponse,
} from "./opportunity-schemas.js";

// Module 09 — THE PIPELINE's HTTP surface (spec 09 §5's P3-E2 PR E2-1 rows;
// D-312). Thirteen routes: the stages read and their tuning, the deal list,
// the board's one read, the weighted forecast, the deal page's read, the five
// deal writes (create, edit, move, win, lose) plus reopen, and the quote's
// link to its deal. The task surface's two new rows (PATCH and withdraw) and
// the amended rows (the quote create and detail, the customer book and 360,
// the task list and create) are registered where their routes already live
// (`./register.ts`, `./quote-register.ts`).
//
// PERMISSIONS — one code per route (the register's invariant), cell
// narrowings service-side (D-040 two-layer). ONE new code,
// `sales.opportunity.write` ({group_admin, facility_admin, sales}, D-312 ruling
// 15), for every deal write and the link. The reads ride `sales.read`,
// narrowed by the lineage that authorised the call (§7 narrowing 15: ga, fa,
// finance and planner read every deal, a sales principal the deals it owns and
// its assigned customers' deals, every other lineage 403). Stage tuning rides
// `sales.tier.manage`. The forecast is a VALUE DOOR (narrowing 7), and the
// list's value order is fenced (narrowing 12).
//
// ORDERING. Refusals are listed in the order each door meets them (spec 09
// §5). Where a door's 403s come before its 400, the controller checks the
// lineage and the value grant before it parses the query, so a caller who
// may not use the route never learns what a malformed query would say.
// `GET /v1/opportunities/board` and `/forecast` are declared before `/{id}`
// in the controller, so neither literal is read as an id.
//
// EVENTS (§6, D-312 ruling 16): `sales.opportunity_created`,
// `_stage_changed`, `_won`, `_lost` and `_reopened`, each with its emitter.
// Audit payloads carry no money, no title and no note (§7's payload table,
// D-312 ruling 17). Every mutation requires `Idempotency-Key`, and D-303
// serves a replay only to the principal who made the request.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch";
  path: string;
  operationId: string;
  summary: string;
  permission: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["sales"],
    summary: config.summary,
    description: `${config.description} Requires \`${config.permission}\`.`,
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});
const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});
const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const IdParam = z.object({ id: z.guid() });

/** Every door that answers 422 `CUSTOMER_ARCHIVED` says this since P3-E2 PR
 * E2-2b (spec 09 §5's E2-2 row 22). */
const MERGED_EXTENSION =
  "`CUSTOMER_ARCHIVED` carries `extensions.merged_into_customer_id` naming the winner when the customer was merged (spec 09 §5's E2-2 row 22)";

/** The move's 409 body: `OPPORTUNITY_STAGE_CHANGED` carries
 * `current_stage_id` beside `code`. */
const OpportunityMoveConflictProblem = Problem.extend(
  OpportunityMoveConflictExtensions.partial().shape,
).openapi("OpportunityMoveConflictProblem", {
  description:
    "A refused move. On `OPPORTUNITY_STAGE_CHANGED` it carries `current_stage_id`, the stage the deal is in, so the board can name it and put the card there (spec 09 §4.14). Absent on every other code.",
});

const LINEAGE_403 =
  "PERMISSION_DENIED — with no grant named, for a caller whose authorising lineage reads no deal (§7 narrowing 15: qc, storekeeper and any lineage other than group_admin, facility_admin, sales, finance and planner)";

const NARROWING_15 =
  "Narrowed by the lineage that authorised the call (§7 narrowing 15): group_admin, facility_admin, finance and planner read every deal, a sales principal reads the deals it owns and its assigned customers' deals, and every other lineage is refused 403.";

const VALUE_OMISSION =
  "`expected_value_minor` and `weighted_value_minor` ride `sales.read_value` by OMISSION (spec 09 §7, D-312 ruling 12), and NULL is a different fact: nobody estimated the value, or (for the weighted value) the deal is not open. The weighted value is always `sales_weighted_minor(value, pct)`, rounded half-even once per deal (D-312 ruling 10).";

const TWO_DOORS =
  "A label follows its own door (D-312 ruling 21): `customer_name` is null to a reader the customer door refuses, each `won_quote_*` and `won_order_*` pair and `won_order_cancelled` are null to a reader the quote or order door refuses, and `linked_quote_count` counts only the quotes this reader's quote door answers.";

const WRITE_NARROWING =
  "§7 narrowing 16: a principal whose only lineage among group_admin, facility_admin and sales is `sales` writes only the deals it owns and the deals of its assigned customers, even when another lineage such as planner or finance lets it read every deal (it reads those with `mutable` false, and each write door answers it 404 `OPPORTUNITY_NOT_FOUND` on them), and only a literal group_admin or facility_admin names another owner.";

// ===========================================================================
// pipeline stages (spec 09 §2.24, §4.13's card; D-312 ruling 2)
// ===========================================================================

route({
  method: "get",
  path: "/v1/pipeline-stages",
  operationId: "listSalesPipelineStages",
  summary: "The tenant's pipeline stages, by position",
  permission: "sales.read",
  description:
    "Spec 09 §2.24 and §5. The stages of the tenant's one pipeline, in `position` order, at most 50 and unpaginated. Stages are pack data (the `pipeline_stage` kind), adopted by code and never rewritten by an install, so no route adds, renames, reorders or removes one. `name_i18n` renders in the reader's language. An empty list means the tenant's installed pack predates the kind (D-312 ruling 3): a fresh install, a forced re-install before go-live or the seed gives it stages, and a live tenant waits for the pack-upgrade slice. " +
    NARROWING_15,
  responses: {
    ...ok("The stages, by position", PipelineStageListResponse),
    403: problemResponse(LINEAGE_403),
  },
});

route({
  method: "patch",
  path: "/v1/pipeline-stages/{id}",
  operationId: "updateSalesPipelineStage",
  summary: "Tune a stage's probability or rotting days",
  permission: "sales.tier.manage",
  description:
    "Spec 09 §4.13's Pipeline stages card and §5 (D-312 ruling 2). A tenant tunes `probability_pct` (0 to 100, never null) and `rotting_after_days` (1 to 365, or null: a deal never rots in that stage) and nothing else about a stage, and a later pack install leaves the tuning alone. At least one key is required. Probability is LIVE: the board, the list, the forecast and the agent tool read the stage's current value, so a change moves every open deal's weighted value at once. Audited `sales_pipeline_stage.updated` with the changed field names and `from`/`to` for both numbers. No event. The door refuses 404 `PIPELINE_STAGE_NOT_FOUND` first, then 400 `VALIDATION_FAILED`.",
  request: { params: IdParam, ...jsonBody(PipelineStageUpdateRequest) },
  responses: {
    ...ok("The tuned stage", PipelineStage),
    400: problemResponse(
      "VALIDATION_FAILED — no key, a key outside the two, a probability outside 0–100 or null, or rotting days outside 1–365",
    ),
    404: problemResponse(
      "PIPELINE_STAGE_NOT_FOUND — not one of this tenant's stages (another tenant's is invisible)",
    ),
  },
});

// ===========================================================================
// deals — reads (spec 09 §2.25, §3.11, §4.14; §5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/opportunities",
  operationId: "listSalesOpportunities",
  summary: "The deal list (cursor page, typed order)",
  permission: "sales.read",
  description:
    "Spec 09 §4.14 and §5. Filters AND together: `status` (absent means `open`), `stage_id`, `owner_user_id`, `customer_id`, `close_from` and `close_to` (`YYYY-MM`, both inclusive), `rotting=true`, `needs_next_step=true`, `lost_reason` (§2.12's four codes) and `q` over the title, the number and the customer's display name, LIKE-escaped (#209), where the name matches only on deals whose customer the caller's customer door answers (D-312 ruling 21, the D-306 rule). A filter naming a customer, owner or stage the caller cannot read answers an empty page, never a 404. No filter reads a value (spec 16 §9.23). `order` is the typed sort surface: its declared default `expected_close_month` breaks ties by `created_at` then `id`, the board's column order, so a board column's `next_cursor` continues here. The cursor encodes the order it was minted under, and a cursor presented under a different effective order answers 400 `invalid_cursor`. The door refuses 403 for the lineage first, then 403 `missing_permission: sales.read_value` for `order=expected_value_minor` or `order=-expected_value_minor` in the controller before the cursor is decoded (§7 narrowing 12, D-301), then 400. " +
    NARROWING_15 +
    " " +
    VALUE_OMISSION +
    " " +
    TWO_DOORS,
  request: { query: OpportunityListQuery },
  responses: {
    ...ok("Deals, in the order asked for", OpportunityListResponse),
    400: problemResponse(
      "VALIDATION_FAILED — a filter outside its enum, an id that is not a uuid, a month that is not `YYYY-MM`, `rotting` or `needs_next_step` other than `true`, or a malformed cursor (`invalid_cursor`, also for a cursor minted under another order)",
    ),
    403: problemResponse(
      `${LINEAGE_403}; then \`missing_permission: sales.read_value\` for a value order from a caller without the grant (D-301), before the cursor is decoded`,
    ),
  },
});

route({
  method: "get",
  path: "/v1/opportunities/board",
  operationId: "getSalesOpportunityBoard",
  summary: "The board's one read — every stage column at once",
  permission: "sales.read",
  description:
    "Spec 09 §4.14 and §5. One column per stage in position order, each with the whole stage's `open_count` and `rotting_count`, its per-currency `values` for a value reader (the key is omitted without `sales.read_value`; the board never adds up the cards it has loaded), and at most 25 open deals in board order (`expected_close_month`, `created_at`, `id`). Each `next_cursor` is minted in `GET /v1/opportunities`'s cursor shape for that route's default order, so Show more calls `GET /v1/opportunities?status=open&stage_id=<stage>` with the board's own filters, that cursor and `limit=25`, and the second page continues the first with no gap and no repeat. `owner_user_id` and `customer_id` are the two filters the board shares with the list (the board carries no saved views, D-312 ruling 20). `tenant_month` is the month `close_month_passed` is judged against. A tenant with no stages answers `stages: []`. " +
    NARROWING_15 +
    " " +
    VALUE_OMISSION +
    " " +
    TWO_DOORS,
  request: { query: OpportunityBoardQuery },
  responses: {
    ...ok("The board", OpportunityBoardResponse),
    400: problemResponse("VALIDATION_FAILED — a filter that is not a uuid"),
    403: problemResponse(LINEAGE_403),
  },
});

route({
  method: "get",
  path: "/v1/opportunities/forecast",
  operationId: "getSalesOpportunityForecast",
  summary: "The weighted forecast, by close month and currency",
  permission: "sales.read",
  description:
    "Spec 09 §3.11 and §5 (D-312 ruling 10). A VALUE DOOR (§7 narrowing 7): the whole answer is money, so a caller without `sales.read_value` is refused 403 before any query. Per close month and currency it counts the open deals and sums their expected value and their weighted value, each deal's weighted value being `sales_weighted_minor(value, pct)` at the stage's CURRENT probability, rounded half-even once per deal, so the list filtered to a month adds up to its cell exactly. A deal without a value is counted apart (`unvalued_count`, never zero). Won deals count in the tenant month they closed, at the won order's current `total_minor` in the order's currency when an order won them and at their expected value otherwise; a won deal whose order was later cancelled counts in `won_cancelled_count` with no value. For a narrowed reader the order door refuses, such a deal counts at its expected value in the deal's currency, or in `won_unvalued_count`, and never in `won_cancelled_count` (D-312 ruling 21). Open deals whose close month has passed stand on their own `slipped` row per currency. No cell adds two currencies. `from_month` and `to_month` default to the tenant's current month and five months after it, and the resolved pair runs forward and spans at most 24 months. A `customer_id` the caller cannot read answers empty rows. §4.9's rollup keeps a cancelled order in the day it was confirmed on purpose, and the two differ deliberately. The door refuses 403 for the lineage first, then 403 `missing_permission: sales.read_value` before any query, then 400 for the span. " +
    NARROWING_15,
  request: { query: OpportunityForecastQuery },
  responses: {
    ...ok("The forecast", OpportunityForecastResponse),
    400: problemResponse(
      "VALIDATION_FAILED — a month that is not `YYYY-MM`, a filter that is not a uuid, or a resolved range that runs backwards or spans more than 24 months",
    ),
    403: problemResponse(
      `${LINEAGE_403}; then \`missing_permission: sales.read_value\` for a caller without the grant (§7 narrowing 7), before any query`,
    ),
  },
});

route({
  method: "get",
  path: "/v1/opportunities/{id}",
  operationId: "getSalesOpportunity",
  summary: "The deal page's read",
  permission: "sales.read",
  description:
    "Spec 09 §4.14 and §5. The deal with its derived fields and `linked_quotes` (at most 20, newest first, each with its status, validity, rejection reason code and, for a value reader, its total). `mutable` says whether this caller's write doors answer for the deal. A deal outside the caller's narrowing answers the same 404 as one that does not exist. " +
    NARROWING_15 +
    " " +
    VALUE_OMISSION +
    " " +
    TWO_DOORS +
    " `linked_quotes` holds only the quotes this reader's quote door answers.",
  request: { params: IdParam },
  responses: {
    ...ok("The deal", OpportunityDetail),
    403: problemResponse(LINEAGE_403),
    404: problemResponse(
      "OPPORTUNITY_NOT_FOUND — also for a deal outside the caller's narrowing, and another tenant's",
    ),
  },
});

// ===========================================================================
// deals — writes (spec 09 §3.11; §5)
// ===========================================================================

route({
  method: "post",
  path: "/v1/opportunities",
  operationId: "createSalesOpportunity",
  summary: "Create a deal",
  permission: "sales.opportunity.write",
  description:
    "Spec 09 §3.11 and §5 (D-312). The deal is born `open` in `stage_id`, or in the first stage when it is absent, with the caller as `created_by` and the customer's `default_currency` frozen as its currency. `owner_user_id` absent means the caller for a sales principal and the customer's §2.2 owner (else the caller) for an admin or an agent; an eligible owner is an active member holding `sales.opportunity.write` who can read the customer. `quote_id` links an existing quote of the same customer that is not terminal and not linked elsewhere, locked before the deal's insert. `source_conversation_id` is checked through module 17's port inside the caller's transaction. Audited `sales_opportunity.created` (no money: `value_present` says whether a value was set); emits `sales.opportunity_created`. " +
    WRITE_NARROWING +
    " A sales principal creates only on an assigned customer. The door refuses, in this order: 403 `missing_permission: sales.read_value` when a caller without the grant names `expected_value_minor` (null included), 404 `CUSTOMER_NOT_FOUND`, 422 `CUSTOMER_ARCHIVED`, 409 `PIPELINE_NOT_CONFIGURED`, 404 `PIPELINE_STAGE_NOT_FOUND`, 422 `OPPORTUNITY_OWNER_NOT_ELIGIBLE`, 404 `QUOTE_NOT_FOUND`, 409 `OPPORTUNITY_CUSTOMER_MISMATCH`, 409 `QUOTE_TERMINAL`, 409 `QUOTE_LINKED_ELSEWHERE`, and last the port's 404 `CONVERSATION_NOT_FOUND` (an unknown conversation or another tenant's), 409 `OPPORTUNITY_CUSTOMER_MISMATCH` (another customer's conversation, or one with no customer) or 409 `SOURCE_CONVERSATION_UNVERIFIABLE` (the port is unbound; logged as an error). A create without `source_conversation_id` never meets the port.",
  request: jsonBody(OpportunityCreateRequest),
  responses: {
    ...created("The created deal", Opportunity),
    400: problemResponse(
      "VALIDATION_FAILED — a malformed body: a title outside 1–200 characters after trimming, a month that is not `YYYY-MM`, a negative value, an unknown key",
    ),
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission: sales.read_value` when a caller without the grant names `expected_value_minor` (D-312 ruling 12)",
    ),
    404: problemResponse(
      "CUSTOMER_NOT_FOUND / PIPELINE_STAGE_NOT_FOUND / QUOTE_NOT_FOUND / CONVERSATION_NOT_FOUND",
    ),
    409: problemResponse(
      "PIPELINE_NOT_CONFIGURED — the tenant has no stages (D-312 ruling 3); `extensions.remedy` is `apply_pack_update`, the previewed pack update that adds them (`POST /v1/verticals/installs/current/update`, D-343) / OPPORTUNITY_CUSTOMER_MISMATCH — the quote or the source conversation is another customer's, or the conversation has none / QUOTE_TERMINAL / QUOTE_LINKED_ELSEWHERE / SOURCE_CONVERSATION_UNVERIFIABLE",
    ),
    422: problemResponse(`CUSTOMER_ARCHIVED / OPPORTUNITY_OWNER_NOT_ELIGIBLE. ${MERGED_EXTENSION}`),
  },
});

route({
  method: "patch",
  path: "/v1/opportunities/{id}",
  operationId: "updateSalesOpportunity",
  summary: "Edit a deal's title, value, close month or owner",
  permission: "sales.opportunity.write",
  description:
    "Spec 09 §5. At least one key. `expected_value_minor: null` clears the estimate. The value and the close month change only on an open deal, and the title and the owner stay editable on a closed one. The stage never changes here; that is the move. Audited `sales_opportunity.updated` with the changed field names and `from`/`to` for `owner_user_id` and `expected_close_month` only. No event. " +
    WRITE_NARROWING +
    " The door refuses, in this order: 403 `missing_permission: sales.read_value` when a caller without the grant names `expected_value_minor`, 404 `OPPORTUNITY_NOT_FOUND`, 422 `OPPORTUNITY_OWNER_NOT_ELIGIBLE` (a non-admin naming another owner, or an ineligible owner), and 409 `OPPORTUNITY_NOT_OPEN` for the value or the month of a closed deal.",
  request: { params: IdParam, ...jsonBody(OpportunityUpdateRequest) },
  responses: {
    ...ok("The edited deal", Opportunity),
    400: problemResponse("VALIDATION_FAILED — no key, an unknown key, or a malformed value"),
    403: problemResponse(
      "PERMISSION_DENIED — `missing_permission: sales.read_value` when a caller without the grant names `expected_value_minor`",
    ),
    404: problemResponse("OPPORTUNITY_NOT_FOUND"),
    409: problemResponse(
      "OPPORTUNITY_NOT_OPEN — the value or the close month of a won or lost deal",
    ),
    422: problemResponse("OPPORTUNITY_OWNER_NOT_ELIGIBLE"),
  },
});

route({
  method: "post",
  path: "/v1/opportunities/{id}/move",
  operationId: "moveSalesOpportunity",
  summary: "Move a deal to another stage",
  permission: "sales.opportunity.write",
  description:
    "Spec 09 §3.11 and §5. The body names the stage the card left (`from_stage_id`), and the server compares it with the deal under the row lock, so a stale drop never lands: someone who moved the deal first wins, and the late mover meets 409 `OPPORTUNITY_STAGE_CHANGED` with `current_stage_id`. A move to the deal's own stage answers 200 and writes nothing. A move restarts the rotting clock. Audited `sales_opportunity.stage_changed`; emits `sales.opportunity_stage_changed` (never on a same-stage move). " +
    WRITE_NARROWING +
    " The door refuses 404 `OPPORTUNITY_NOT_FOUND` first, then 404 `PIPELINE_STAGE_NOT_FOUND`, then under the row lock 409 `OPPORTUNITY_NOT_OPEN` and 409 `OPPORTUNITY_STAGE_CHANGED`.",
  request: { params: IdParam, ...jsonBody(OpportunityMoveRequest) },
  responses: {
    ...ok("The deal in its new stage (or unchanged, for a same-stage move)", Opportunity),
    404: problemResponse("OPPORTUNITY_NOT_FOUND / PIPELINE_STAGE_NOT_FOUND"),
    409: {
      description:
        "OPPORTUNITY_NOT_OPEN / OPPORTUNITY_STAGE_CHANGED — someone moved the deal first; `current_stage_id` names where it is",
      content: { "application/problem+json": { schema: OpportunityMoveConflictProblem } },
    },
  },
});

route({
  method: "post",
  path: "/v1/opportunities/{id}/win",
  operationId: "winSalesOpportunity",
  summary: "Mark a deal won",
  permission: "sales.opportunity.write",
  description:
    "Spec 09 §3.11 and §5 (D-312 ruling 9). A person wins a deal, optionally naming the business that won it: a CONVERTED quote of the customer, whose `converted_order_id` is then stamped as `won_order_id`, or a confirmed order of the customer, never both. A named quote that is not linked is linked in the same transaction, with the quote locked first. The customer's acceptance of a linked quote wins the deal on its own, inside the §3.5 transaction (step 7a, the system actor), and needs no call here. Audited `sales_opportunity.won`; emits `sales.opportunity_won` with the principal actor. " +
    WRITE_NARROWING +
    " The door refuses, in this order: 404 `OPPORTUNITY_NOT_FOUND`, 404 `QUOTE_NOT_FOUND` or `ORDER_NOT_FOUND`, 409 `OPPORTUNITY_CUSTOMER_MISMATCH`, 409 `QUOTE_NOT_CONVERTED`, 409 `ORDER_NOT_CONFIRMED` or `ORDER_TERMINAL` (a named order, or a named quote's converted order, that is cancelled), 409 `QUOTE_LINKED_ELSEWHERE` (a named quote, or a named order's quote, that belongs to another deal), 409 `ORDER_WON_ELSEWHERE` (the order, named or the quote's, already won another deal: one order backs one deal, so the forecast never counts it twice), then under the lock 409 `OPPORTUNITY_NOT_OPEN`.",
  request: { params: IdParam, ...jsonBody(OpportunityWinRequest) },
  responses: {
    ...ok("The won deal", Opportunity),
    400: problemResponse("VALIDATION_FAILED — both a quote and an order named (`quote_and_order`)"),
    404: problemResponse("OPPORTUNITY_NOT_FOUND / QUOTE_NOT_FOUND / ORDER_NOT_FOUND"),
    409: problemResponse(
      "OPPORTUNITY_CUSTOMER_MISMATCH / QUOTE_NOT_CONVERTED / ORDER_NOT_CONFIRMED / ORDER_TERMINAL / QUOTE_LINKED_ELSEWHERE / ORDER_WON_ELSEWHERE / OPPORTUNITY_NOT_OPEN",
    ),
  },
});

route({
  method: "post",
  path: "/v1/opportunities/{id}/lose",
  operationId: "loseSalesOpportunity",
  summary: "Mark a deal lost",
  permission: "sales.opportunity.write",
  description:
    "Spec 09 §3.11 and §5 (D-312 ruling 9). One of §2.12's four reasons (`price`, `lead_time`, `stock`, `other`), so a deal's loss and a quote's rejection read one vocabulary, and an optional note of at most 2000 characters stored as typed. Losing a deal leaves its quotes and its tasks as they are. Audited `sales_opportunity.lost` (the reason and `note_present`, never the note); emits `sales.opportunity_lost`. " +
    WRITE_NARROWING +
    " The door refuses 404 `OPPORTUNITY_NOT_FOUND` first and then 409 `OPPORTUNITY_NOT_OPEN`.",
  request: { params: IdParam, ...jsonBody(OpportunityLoseRequest) },
  responses: {
    ...ok("The lost deal", Opportunity),
    404: problemResponse("OPPORTUNITY_NOT_FOUND"),
    409: problemResponse("OPPORTUNITY_NOT_OPEN"),
  },
});

route({
  method: "post",
  path: "/v1/opportunities/{id}/reopen",
  operationId: "reopenSalesOpportunity",
  summary: "Reopen a won or lost deal",
  permission: "sales.opportunity.write",
  description:
    "Spec 09 §3.11 and §5 (D-312 ruling 9). Takes no body. Reopens a deal a person won, a lost deal, or a deal the customer's acceptance won once that order has been cancelled, because the business the acceptance recorded did not happen. Reopening clears the win stamps, returns the deal to the stage it closed from and restarts its clock; the linked quotes stay linked. Audited `sales_opportunity.reopened`; emits `sales.opportunity_reopened`. " +
    WRITE_NARROWING +
    " The door refuses 404 `OPPORTUNITY_NOT_FOUND` first, then 409 `OPPORTUNITY_NOT_CLOSED`, then under the deal lock 409 `OPPORTUNITY_WON_BY_ORDER` while a deal won by acceptance has an order that is not cancelled, its won order or the order of any other linked quote the customer accepted. That refusal tells a writer the order stands even when the order door refuses them, the residual D-312 ruling 21 accepts. Then 422 `CUSTOMER_ARCHIVED` when the deal's customer has been archived since it closed, because an archived customer holds no live business (§3.1 item 4). The customer is read `FOR KEY SHARE`, as the create reads it, so an archive racing the reopen waits and then answers 409 `CUSTOMER_HAS_OPEN_DOCS`.",
  request: { params: IdParam },
  responses: {
    ...ok("The reopened deal", Opportunity),
    404: problemResponse("OPPORTUNITY_NOT_FOUND"),
    409: problemResponse(
      "OPPORTUNITY_NOT_CLOSED — the deal is open / OPPORTUNITY_WON_BY_ORDER — the customer's acceptance won it and an order one of its accepted quotes minted still stands",
    ),
    422: problemResponse(
      `CUSTOMER_ARCHIVED — the deal's customer has been archived since the deal closed. ${MERGED_EXTENSION}`,
    ),
  },
});

// ===========================================================================
// the quote's link to its deal (spec 09 §2.12 `opportunity_id`; §5)
// ===========================================================================

route({
  method: "post",
  path: "/v1/quotes/{id}/opportunity",
  operationId: "linkSalesQuoteOpportunity",
  summary: "Link a quote to a deal, or unlink it",
  permission: "sales.opportunity.write",
  description:
    "Spec 09 §2.12 and §5 (D-312 ruling 4). The link lives on the quote, and one deal holds many quotes. The caller must read the quote, and §7 narrowing 16's write door must admit the deal, for a link and an unlink alike: a deal the caller reads and cannot write answers 404 `OPPORTUNITY_NOT_FOUND`. " +
    WRITE_NARROWING +
    " The door locks the quote and then reads the deal `FOR SHARE`, the acceptance's own order, so a loss cannot close the deal between the check and the commit (§3.5 step 7a). A link refuses, in this order, 404 `QUOTE_NOT_FOUND`, 404 `OPPORTUNITY_NOT_FOUND`, 409 `OPPORTUNITY_CUSTOMER_MISMATCH`, 409 `QUOTE_TERMINAL`, 409 `OPPORTUNITY_NOT_OPEN` and 409 `QUOTE_LINKED_ELSEWHERE`, so re-pointing a linked quote needs an unlink first. `opportunity_id: null` unlinks: it refuses 404 `QUOTE_NOT_FOUND`, then 404 `OPPORTUNITY_NOT_FOUND` when the linked deal is outside the caller's write door, then 409 `QUOTE_TERMINAL` for a quote in a terminal status, because a finished quote's link is part of its deal's history. The deal's status never refuses an unlink, so an open quote on a lost or won deal can move to another deal. Unlinking a quote with no deal answers 200 and writes nothing. Audited `sales_opportunity.quote_linked` or `sales_opportunity.quote_unlinked` with entity `sales_quote`. No event.",
  request: { params: IdParam, ...jsonBody(QuoteOpportunityLinkRequest) },
  responses: {
    ...ok("The quote's link as it now stands", QuoteOpportunityLinkResponse),
    400: problemResponse("VALIDATION_FAILED — `opportunity_id` missing, or not a uuid or null"),
    404: problemResponse(
      "QUOTE_NOT_FOUND / OPPORTUNITY_NOT_FOUND — also for a deal the caller reads and cannot write (narrowing 16)",
    ),
    409: problemResponse(
      "OPPORTUNITY_CUSTOMER_MISMATCH / QUOTE_TERMINAL / OPPORTUNITY_NOT_OPEN / QUOTE_LINKED_ELSEWHERE",
    ),
  },
});
